import { NotificationPreference, NotificationCategory, UserNotificationPreferences } from '../types';

import { bearerAuthHeader } from './apiHeaders';
/**
 * Default notification preferences for new users
 */
export const getDefaultPreferences = (): NotificationPreference[] => {
  return [
    {
      category: 'status_changes',
      channels: { inapp: true, email: true, sms: false },
      enabled: true,
    },
    {
      category: 'jsm_assessment',
      channels: { inapp: true, email: true, sms: false },
      enabled: true,
    },
    {
      category: 'comments',
      channels: { inapp: true, email: true, sms: false },
      enabled: true,
    },
    {
      category: 'consultant_reviews',
      channels: { inapp: true, email: true, sms: false },
      enabled: true,
    },
    {
      category: 'deadlines',
      channels: { inapp: true, email: true, sms: false },
      enabled: true,
    },
    {
      category: 'system',
      channels: { inapp: true, email: false, sms: false },
      enabled: true,
    },
  ];
};

/**
 * Get human-readable category names
 */
export const getCategoryDisplayName = (category: NotificationCategory): string => {
  const displayNames: Record<NotificationCategory, string> = {
    status_changes: 'Status Changes',
    jsm_assessment: 'JSM Assessments',
    comments: 'Comments & Mentions',
    consultant_reviews: 'Consultant Reviews',
    deadlines: 'Deadline Reminders',
    system: 'System Notifications',
  };
  return displayNames[category];
};

/**
 * Get category description
 */
export const getCategoryDescription = (category: NotificationCategory): string => {
  const descriptions: Record<NotificationCategory, string> = {
    status_changes: 'Configuration item status updates (in-progress, ready-for-build, approved, etc.)',
    jsm_assessment: 'Updates on JSM Project Design Assessments and consultant responses',
    comments: 'When someone mentions you or comments on your items',
    consultant_reviews: 'When a consultant reviews your configuration or leaves feedback',
    deadlines: 'Reminders for upcoming deadlines on configuration items',
    system: 'Important system announcements and maintenance notifications',
  };
  return descriptions[category];
};

/**
 * Map notification type to category
 */
export const getNotificationCategory = (type: string): NotificationCategory => {
  const typeMapping: Record<string, NotificationCategory> = {
    'ready_for_implementation': 'status_changes',
    'ready_for_build': 'status_changes',
    'ready_for_review': 'status_changes',
    'status_change': 'status_changes',
    'jsm_assessment': 'jsm_assessment',
    'comment_mention': 'comments',
    'homework_assigned': 'consultant_reviews',
    'deadline_reminder': 'deadlines',
    'message': 'system',
  };
  return typeMapping[type] || 'system';
};

/**
 * Check if notification should be shown based on user preferences.
 *
 * The live route (GET /notification-preferences/:userId) answers with per-channel switches,
 * `{ preferences: { email, slack, inApp: { enabled, all } } }`, not the category list this type
 * describes; that shape turns in-app notifications off only when `inApp.enabled` is false.
 * Any other shape shows everything.
 */
export const shouldShowNotification = (
  notificationType: string,
  userPreferences: UserNotificationPreferences | null,
  channel: 'inapp' | 'email' | 'sms' = 'inapp'
): boolean => {
  // If no preferences set, use defaults (show all)
  const saved: unknown = userPreferences?.preferences;
  if (!saved) {
    return true;
  }

  if (!Array.isArray(saved)) {
    if (channel !== 'inapp') return true;
    const inApp = (saved as { inApp?: unknown }).inApp;
    if (inApp === false) return false;
    return !(inApp && typeof inApp === 'object' && (inApp as { enabled?: unknown }).enabled === false);
  }

  const category = getNotificationCategory(notificationType);
  const preference = (saved as NotificationPreference[]).find(p => p.category === category);

  // If category not found in preferences, show by default
  if (!preference) {
    return true;
  }

  // Check if category is enabled and channel is enabled
  return preference.enabled && preference.channels[channel];
};

/**
 * The notifications a user chose to see in the app (the old NotificationCenter applied this;
 * RESTORE_PLAN R2.5). With no preferences saved, everything shows.
 */
export const visibleInApp = <T extends { type?: string }>(
  notifications: T[],
  userPreferences: UserNotificationPreferences | null
): T[] => notifications.filter((n) => shouldShowNotification(String(n.type ?? ''), userPreferences, 'inapp'));

/** Unread notifications the user chose to see in the app (the bell's count). */
export const inAppUnreadCount = (
  notifications: Array<{ type?: string; read?: boolean }>,
  userPreferences: UserNotificationPreferences | null
): number => visibleInApp(notifications, userPreferences).filter((n) => !n.read).length;

/**
 * Fetch user notification preferences from backend
 */
export const fetchUserPreferences = async (
  userId: string,
  projectId: string,
  publicAnonKey: string
): Promise<UserNotificationPreferences | null> => {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/notification-preferences/${userId}`,
      {
        headers: {
          ...bearerAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null; // No preferences set yet
      }
      throw new Error(`Failed to fetch preferences: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching notification preferences:', error);
    return null;
  }
};

/**
 * Save user notification preferences to backend
 */
export const saveUserPreferences = async (
  userId: string,
  preferences: NotificationPreference[],
  projectId: string,
  publicAnonKey: string
): Promise<boolean> => {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/notification-preferences/${userId}`,
      {
        method: 'PUT',
        headers: {
          ...bearerAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ preferences }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to save preferences: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('Error saving notification preferences:', error);
    return false;
  }
};
